import { FirestoreDataConverter, QueryConstraint, query, collection, collectionGroup, getDocs, doc, setDoc, deleteDoc, getDoc, updateDoc, serverTimestamp, where, orderBy, limit } from "firebase/firestore";
import { analytics, auth, db } from "./firebase";
import { logEvent } from "firebase/analytics";
import { Globals } from "../globals";
import { PLAN_FIELDS, PlanFields, planFieldsOf, plannedServiceDocId } from "../utils/sermonPlanDiff";
import { gemeindeSubPath } from "./gemeindePaths";
import ChoirSongPerformance, { choirSongPerformanceConverter } from "../DTOs/ChoirSongPerformance";
import { choirSongConverter } from "../DTOs/ChoirSong";
import { normalizeSongTitle, songDocId, songKey } from "../utils/songTitle";
import PlannedSermon from "../DTOs/PlannedSermon";
import SundayService, { sundayServiceConverter } from "../DTOs/SundayService";
import Holiday, { holidayConverter } from "../DTOs/Holiday";

/**
 * Multi-tenant Firestore data layer. Every function is scoped to one Gemeinde
 * (tenant): the data lives in gemeinden/{gemeindeId}/<subcollection>. Callers
 * obtain the active gemeindeId from the GemeindeContext (useGemeinde()).
 */

/**
 * Retrieves every (matching) doc from a Gemeinde subcollection and converts them.
 *
 * @param gemeindeId active Gemeinde (tenant) id
 * @param subcollection subcollection segment name (see `Globals.*_SUBCOLLECTION`)
 * @param converter converter for the generic type
 * @param queryConstraint a constraint created by the firebase function, e.g. {@link where()}
 * @returns a promise that resolves to every matching item in the subcollection
 */
async function getAllDocsFromCollection<T>(
    gemeindeId: string,
    subcollection: string,
    converter: FirestoreDataConverter<T>,
    ...queryConstraint: QueryConstraint[]
): Promise<T[]> {
    logEvent(analytics, "view_item_list")
    const q = query(
        collection(db, ...gemeindeSubPath(gemeindeId, subcollection)).withConverter(converter),
        ...queryConstraint
    );
    const itemSnap = await getDocs(q);

    const itemArray: T[] = [];
    itemSnap.forEach((item) => {
        itemArray.push(item.data());
    });

    return itemArray;
}

/**
 * Uploads a new item to a Gemeinde subcollection with an auto-generated id.
 *
 * @param gemeindeId active Gemeinde (tenant) id
 * @param subcollection subcollection segment name (see `Globals.*_SUBCOLLECTION`)
 * @param converter converter for the generic type
 * @param item the new item to upload. Must be of the provided type `T`
 * @returns a promise that resolves when the upload is complete
 */
async function uploadItemToFirestore<T>(
    gemeindeId: string,
    subcollection: string,
    converter: FirestoreDataConverter<T>,
    item: T
): Promise<void> {
    const newItem = doc(collection(db, ...gemeindeSubPath(gemeindeId, subcollection))).withConverter(converter);
    await setDoc(newItem, item);
}

/**
 * Updates an existing document in a Gemeinde subcollection.
 *
 * @param gemeindeId active Gemeinde (tenant) id
 * @param subcollection subcollection segment name (see `Globals.*_SUBCOLLECTION`)
 * @param docId the document ID to update
 * @param converter converter for the generic type
 * @param item the updated item data
 * @returns a promise that resolves when the update is complete
 */
async function updateItemInFirestore<T>(
    gemeindeId: string,
    subcollection: string,
    docId: string,
    converter: FirestoreDataConverter<T>,
    item: T
): Promise<void> {
    const docRef = doc(db, ...gemeindeSubPath(gemeindeId, subcollection, docId)).withConverter(converter);
    await setDoc(docRef, item, { merge: true });
}

/**
 * Deletes a document from a Gemeinde subcollection.
 *
 * @param gemeindeId active Gemeinde (tenant) id
 * @param subcollection subcollection segment name (see `Globals.*_SUBCOLLECTION`)
 * @param docId the document ID to delete
 * @returns a promise that resolves when the deletion is complete
 */
async function deleteItemFromFirestore(
    gemeindeId: string,
    subcollection: string,
    docId: string
): Promise<void> {
    const docRef = doc(db, ...gemeindeSubPath(gemeindeId, subcollection, docId));
    await deleteDoc(docRef);
}

/**
 * Adds a choir song performance for a Gemeinde (CSV import). The song is found
 * through the shared title normalisation (src/utils/songTitle.ts): by its
 * document id, then by its case-insensitive key; only if neither exists a new
 * song is created.
 *
 * @param gemeindeId active Gemeinde (tenant) id
 * @param songName the name of the song
 * @param performance the performance data to add
 * @returns a promise that resolves when the operation is complete
 */
async function addChoirSongPerformance(
    gemeindeId: string,
    songName: string,
    performance: ChoirSongPerformance
): Promise<void> {
    const key = songKey(songName);
    if (!key) {
        throw new Error("Invalid song name");
    }
    const songs = collection(db, ...gemeindeSubPath(gemeindeId, Globals.CHOIRSONG_SUBCOLLECTION));
    let songId = songDocId(songName);
    if (!(await getDoc(doc(songs, songId))).exists()) {
        const sameSong = await getDocs(query(songs, where("nameKey", "==", key), limit(1)));
        if (sameSong.empty) {
            await setDoc(doc(songs, songId), { name: normalizeSongTitle(songName), nameKey: key });
        } else {
            songId = sameSong.docs[0].id;
        }
    }

    // Deterministic ID based on date and number: the same song on the same day
    // is one document, whichever source wrote it (see functions/src/choirPerformances.ts).
    performance.gemeindeId = gemeindeId;
    performance.source = "import";
    const performanceId = `${performance.date.toString()}_${performance.number}`;
    const performanceDocRef = doc(
        songs,
        songId,
        Globals.CHOIRSONG_PERFORMANCES_SUBCOLLECTION,
        performanceId
    ).withConverter(choirSongPerformanceConverter);

    await setDoc(performanceDocRef, performance, { merge: true });
}

/**
 * Deletes a specific performance from a choir song. The song itself stays in
 * the catalogue (it is archived or merged in the repertoire view).
 *
 * @param gemeindeId active Gemeinde (tenant) id
 * @param songDocId the document ID of the song
 * @param performanceId the document ID of the performance to delete
 * @returns a promise that resolves when the deletion is complete
 */
async function deleteChoirSongPerformance(
    gemeindeId: string,
    songDocId: string,
    performanceId: string
): Promise<void> {
    await deleteDoc(doc(
        db,
        ...gemeindeSubPath(gemeindeId, Globals.CHOIRSONG_SUBCOLLECTION, songDocId, Globals.CHOIRSONG_PERFORMANCES_SUBCOLLECTION, performanceId)
    ));
}

/**
 * Saves a planned sermon as a SundayService in the Gemeinde's sundayservices
 * subcollection. Only creates a new service if none exists for the same date and
 * service type (deterministic doc ID makes this idempotent and race-free).
 *
 * @param gemeindeId active Gemeinde (tenant) id
 * @param sermon the PlannedSermon to create a service from
 * @returns the new document ID, or null if a service already exists for that date
 */
/**
 * Loads the plan fields (preachers, slot modes, label) of the service a plan row
 * maps to, or null if no such service exists yet. Used by the re-import preview.
 */
async function getExistingPlanFields(
    gemeindeId: string,
    date: string,
    serviceType: PlannedSermon["serviceType"]
): Promise<PlanFields | null> {
    const snap = await getDoc(
        doc(db, ...gemeindeSubPath(gemeindeId, Globals.SUNDAY_SERVICE_SUBCOLLECTION, plannedServiceDocId(date, serviceType)))
    );
    return snap.exists() ? planFieldsOf(snap.data()) : null;
}

/**
 * Applies plan changes from a re-import to an existing service. Only the given
 * plan fields are written (updateDoc); topic, Bible passage, prayer concerns,
 * confirmations and choir songs stay exactly as they are.
 */
async function updateSundayServicePlanFields(
    gemeindeId: string,
    date: string,
    serviceType: PlannedSermon["serviceType"],
    changes: Partial<PlanFields>
): Promise<void> {
    const allowed: Record<string, string> = {};
    for (const field of PLAN_FIELDS) {
        const value = changes[field];
        if (value !== undefined) allowed[field] = value;
    }
    if (Object.keys(allowed).length === 0) return;
    await updateDoc(
        doc(db, ...gemeindeSubPath(gemeindeId, Globals.SUNDAY_SERVICE_SUBCOLLECTION, plannedServiceDocId(date, serviceType))),
        { ...allowed, changedBy: auth.currentUser?.uid ?? "", changedAt: serverTimestamp() }
    );
}

async function saveSundayServiceFromPlannedSermon(gemeindeId: string, sermon: PlannedSermon): Promise<string | null> {
    // Parse the date string directly to avoid UTC vs local-timezone issues.
    const [year, month, day] = sermon.date.split('-').map(Number);
    const serviceDate = new Date(Date.UTC(year, month - 1, day));

    const userId = auth.currentUser?.uid ?? "";
    const now = new Date();
    const sundayService = new SundayService(
        serviceDate,
        "", 0,
        "", 0,
        "", 0,
        "", 0,
        sermon.sermon1PreacherKey,
        "",
        "",
        sermon.sermon1Mode,
        sermon.sermon2PreacherKey,
        "",
        "",
        sermon.sermon2Mode,
        userId,
        now,
        userId,
        now,
        sermon.serviceType,
        sermon.serviceLabel,
        sermon.weekday,
        undefined, // sermon1ConfirmedAt
        undefined, // sermon2ConfirmedAt
        "", // prayerConcern
        [], // prayerConcerns
        undefined, // afternoonMode
        sermon.verkuendiger,
        sermon.guestPreacherOrigin,
        sermon.afternoonEvent,
        sermon.choirInfo,
        sermon.serviceNote
    );

    // Use a deterministic document ID to make the write idempotent and race-free.
    const serviceDocId = plannedServiceDocId(sermon.date, sermon.serviceType);
    const serviceDocRef = doc(db, ...gemeindeSubPath(gemeindeId, Globals.SUNDAY_SERVICE_SUBCOLLECTION, serviceDocId)).withConverter(sundayServiceConverter);
    const existingDoc = await getDoc(serviceDocRef);
    if (existingDoc.exists()) {
        return null;
    }

    await setDoc(serviceDocRef, sundayService);
    return serviceDocId;
}

/**
 * Retrieves all future planned SundayServices of a Gemeinde, sorted ascending.
 *
 * @param gemeindeId active Gemeinde (tenant) id
 * @returns a promise that resolves to SundayService objects with their document IDs
 */
async function getPlannedSundayServices(gemeindeId: string): Promise<Array<SundayService & { docId: string }>> {
    logEvent(analytics, "view_item_list");
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const q = query(
        collection(db, ...gemeindeSubPath(gemeindeId, Globals.SUNDAY_SERVICE_SUBCOLLECTION)).withConverter(sundayServiceConverter),
        where("date", ">=", startOfToday),
        orderBy("date", "asc")
    );
    const snapshot = await getDocs(q);
    const results: Array<SundayService & { docId: string }> = [];
    snapshot.forEach((d) => {
        results.push({ ...d.data(), docId: d.id });
    });
    return results;
}

/**
 * Saves a holiday to the Gemeinde's feiertage subcollection. Uses a deterministic
 * document ID so re-importing the same plan overwrites rather than duplicates.
 *
 * @param gemeindeId active Gemeinde (tenant) id
 * @param holiday the Holiday to save
 * @returns a promise that resolves to the document ID
 */
async function saveHoliday(gemeindeId: string, holiday: Holiday): Promise<string> {
    const holidayDocId = `feiertag_${holiday.date}`;
    const holidayDocRef = doc(db, ...gemeindeSubPath(gemeindeId, Globals.FEIERTAGE_SUBCOLLECTION, holidayDocId)).withConverter(
        holidayConverter
    );
    await setDoc(holidayDocRef, holiday);
    return holidayDocId;
}

/**
 * Retrieves all stored holidays of a Gemeinde from today onwards, ascending.
 *
 * @param gemeindeId active Gemeinde (tenant) id
 * @returns a promise that resolves to Holiday objects with their document IDs
 */
async function getHolidays(gemeindeId: string): Promise<Array<Holiday & { docId: string }>> {
    logEvent(analytics, "view_item_list");
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const q = query(
        collection(db, ...gemeindeSubPath(gemeindeId, Globals.FEIERTAGE_SUBCOLLECTION)).withConverter(holidayConverter),
        where("date", ">=", startOfToday),
        orderBy("date", "asc")
    );
    const snapshot = await getDocs(q);
    const results: Array<Holiday & { docId: string }> = [];
    snapshot.forEach((d) => {
        results.push({ ...d.data(), docId: d.id });
    });
    return results;
}

/**
 * Retrieves the SundayService for today (local date) of a Gemeinde, if any.
 *
 * @param gemeindeId active Gemeinde (tenant) id
 * @returns the service with its document ID, or null if none found
 */
async function getTodaySundayService(gemeindeId: string): Promise<(SundayService & { id: string }) | null> {
    const d = new Date();
    // Construct local midnight boundaries directly to avoid UTC parsing issues.
    const startOfToday = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const startOfTomorrow = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);

    const q = query(
        collection(db, ...gemeindeSubPath(gemeindeId, Globals.SUNDAY_SERVICE_SUBCOLLECTION)).withConverter(sundayServiceConverter),
        where("date", ">=", startOfToday),
        where("date", "<", startOfTomorrow),
        orderBy("date", "asc"),
        limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const docSnap = snapshot.docs[0];
    return { ...docSnap.data(), id: docSnap.id };
}

/**
 * Whether the Gemeinde's performances carry `gemeindeId`, `year` and `month`
 * (set by the choir backfill). Only then the collection-group queries below
 * see every performance; until then the admin view reads song by song.
 */
async function isChoirIndexReady(gemeindeId: string): Promise<boolean> {
    try {
        const marker = await getDoc(
            doc(db, ...gemeindeSubPath(gemeindeId, Globals.GEMEINDE_CONFIG_SUBCOLLECTION, Globals.CHOIR_INDEX_DOC_ID))
        );
        return marker.exists();
    } catch {
        // The marker is only readable for admins; without it the slow path still works.
        return false;
    }
}

/** All performances of one Gemeinde across its songs; always filtered by gemeindeId (tenant isolation). */
function performancesOfGemeinde(gemeindeId: string, ...constraints: QueryConstraint[]) {
    return query(
        collectionGroup(db, Globals.CHOIRSONG_PERFORMANCES_SUBCOLLECTION),
        where("gemeindeId", "==", gemeindeId),
        ...constraints
    );
}

/**
 * Retrieves the years of a Gemeinde that can have choir performances: every
 * year from the first to the last performance (two documents read).
 *
 * @param gemeindeId active Gemeinde (tenant) id
 * @returns a promise that resolves to an array of years (sorted descending)
 */
async function getAvailableChoirSongYears(gemeindeId: string): Promise<number[]> {
    if (!(await isChoirIndexReady(gemeindeId))) {
        return getAvailableChoirSongYearsLegacy(gemeindeId);
    }
    logEvent(analytics, "view_item_list");
    const [first, last] = await Promise.all([
        getDocs(performancesOfGemeinde(gemeindeId, orderBy("year", "asc"), limit(1))),
        getDocs(performancesOfGemeinde(gemeindeId, orderBy("year", "desc"), limit(1))),
    ]);
    if (first.empty || last.empty) {
        return [];
    }
    const years: number[] = [];
    for (let year = Number(last.docs[0].data().year); year >= Number(first.docs[0].data().year); year--) {
        years.push(year);
    }
    return years;
}

/** Reads every song and performance; only used until the choir backfill has run. */
async function getAvailableChoirSongYearsLegacy(gemeindeId: string): Promise<number[]> {
    logEvent(analytics, "view_item_list");

    const songsSnapshot = await getDocs(
        collection(db, ...gemeindeSubPath(gemeindeId, Globals.CHOIRSONG_SUBCOLLECTION)).withConverter(choirSongConverter)
    );

    const yearsSet = new Set<number>();
    const performanceSnapshots = await Promise.all(
        songsSnapshot.docs.map(songDoc =>
            getDocs(
                collection(db, ...gemeindeSubPath(gemeindeId, Globals.CHOIRSONG_SUBCOLLECTION, songDoc.id, Globals.CHOIRSONG_PERFORMANCES_SUBCOLLECTION))
                    .withConverter(choirSongPerformanceConverter)
            )
        )
    );

    performanceSnapshots.forEach(performancesSnapshot => {
        performancesSnapshot.docs.forEach(perfDoc => {
            const perf = perfDoc.data();
            if (perf.year) {
                yearsSet.add(perf.year);
            } else {
                // Fallback for old data without year field
                const dateStr = typeof perf.date === 'string' ? perf.date : perf.date?.toString?.() || '';
                if (dateStr) {
                    const parsedYear = parseInt(dateStr.substring(0, 4));
                    if (!isNaN(parsedYear)) {
                        yearsSet.add(parsedYear);
                    }
                }
            }
        });
    });

    return Array.from(yearsSet).sort((a, b) => b - a);
}

type MonthlyPerformance = {
    songName: string;
    songDocId: string;
    performanceDocId: string;
    date: string;
    occasion?: string;
};
type PerformancesByMonth = { months: Map<number, MonthlyPerformance[]>; availableMonths: number[] };

/** Newest date first, then song name; months newest first. */
function sortedByMonth(months: Map<number, MonthlyPerformance[]>): PerformancesByMonth {
    months.forEach((performances) => {
        performances.sort((a, b) => b.date.localeCompare(a.date) || a.songName.localeCompare(b.songName, "de"));
    });
    return { months, availableMonths: Array.from(months.keys()).sort((a, b) => b - a) };
}

/**
 * Retrieves all choir performances of a Gemeinde for a year, grouped by month.
 * Reads only that year's performances and the songs they belong to.
 *
 * @param gemeindeId active Gemeinde (tenant) id
 * @param year the year to filter by (e.g., 2025)
 */
async function getChoirSongPerformancesByYearGroupedByMonth(
    gemeindeId: string,
    year: number
): Promise<PerformancesByMonth> {
    if (!(await isChoirIndexReady(gemeindeId))) {
        return getChoirSongPerformancesByYearGroupedByMonthLegacy(gemeindeId, year);
    }
    logEvent(analytics, "view_item_list");

    const snapshot = await getDocs(performancesOfGemeinde(gemeindeId, where("year", "==", year)));
    const songIds = Array.from(new Set(snapshot.docs.map((perfDoc) => perfDoc.ref.parent.parent!.id)));
    const songNames = new Map<string, string>();
    await Promise.all(
        songIds.map(async (songId) => {
            const song = await getDoc(doc(db, ...gemeindeSubPath(gemeindeId, Globals.CHOIRSONG_SUBCOLLECTION, songId)));
            songNames.set(songId, String(song.data()?.name ?? songId));
        })
    );

    const months = new Map<number, MonthlyPerformance[]>();
    snapshot.docs.forEach((perfDoc) => {
        const perf = perfDoc.data();
        const month = Number(perf.month);
        if (!month) {
            return;
        }
        const songId = perfDoc.ref.parent.parent!.id;
        months.set(month, [
            ...(months.get(month) ?? []),
            {
                songName: songNames.get(songId) ?? songId,
                songDocId: songId,
                performanceDocId: perfDoc.id,
                date: String(perf.date),
                occasion: perf.occasion ? String(perf.occasion) : undefined,
            },
        ]);
    });
    return sortedByMonth(months);
}

/** Reads every song and performance; only used until the choir backfill has run. */
async function getChoirSongPerformancesByYearGroupedByMonthLegacy(
    gemeindeId: string,
    year: number
): Promise<PerformancesByMonth> {
    logEvent(analytics, "view_item_list");

    const songsSnapshot = await getDocs(
        collection(db, ...gemeindeSubPath(gemeindeId, Globals.CHOIRSONG_SUBCOLLECTION)).withConverter(choirSongConverter)
    );

    const months = new Map<number, MonthlyPerformance[]>();

    const performanceSnapshots = await Promise.all(
        songsSnapshot.docs.map(songDoc =>
            getDocs(
                collection(db, ...gemeindeSubPath(gemeindeId, Globals.CHOIRSONG_SUBCOLLECTION, songDoc.id, Globals.CHOIRSONG_PERFORMANCES_SUBCOLLECTION))
                    .withConverter(choirSongPerformanceConverter)
            ).then(snapshot => ({ songDoc, snapshot }))
        )
    );

    performanceSnapshots.forEach(({ songDoc, snapshot }) => {
        const songDocId = songDoc.id;
        const songName = songDoc.data().name.toString();

        snapshot.docs.forEach(perfDoc => {
            const perf = perfDoc.data();
            const dateStr = typeof perf.date === 'string' ? perf.date : perf.date?.toString?.() || '';

            let perfYear = perf.year;
            if (!perfYear && dateStr) {
                perfYear = parseInt(dateStr.substring(0, 4));
            }

            if (perfYear !== year) {
                return;
            }

            let month = perf.month;
            if (!month && dateStr) {
                month = parseInt(dateStr.substring(5, 7));
            }

            if (!month || isNaN(month)) {
                return;
            }

            if (!months.has(month)) {
                months.set(month, []);
            }

            months.get(month)!.push({
                songName,
                songDocId,
                performanceDocId: perfDoc.id,
                date: dateStr,
                occasion: perf.occasion?.toString()
            });
        });
    });

    return sortedByMonth(months);
}

export {
    getAllDocsFromCollection,
    uploadItemToFirestore,
    updateItemInFirestore,
    deleteItemFromFirestore,
    addChoirSongPerformance,
    deleteChoirSongPerformance,
    saveSundayServiceFromPlannedSermon,
    getExistingPlanFields,
    updateSundayServicePlanFields,
    getPlannedSundayServices,
    saveHoliday,
    getHolidays,
    getTodaySundayService,
    getAvailableChoirSongYears,
    getChoirSongPerformancesByYearGroupedByMonth,
};
